import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Shell from '../Shell';
import { api } from '../api';
import { useToast } from '../ToastContext';

const STATUS_CHIP = {
  New: 'muted',
  Structured: 'primary',
  SentToSuppliers: 'info',
  AwaitingQuotes: 'review',
  PartialQuotes: 'review',
  ReadyForComparison: 'ready',
  DecisionMade: 'info',
  CustomerQuoteSent: 'info',
  Won: 'ready',
  Lost: 'risk',
  ClosedNoQuote: 'muted',
  OnHold: 'muted',
  Cancelled: 'muted',
};

const SORT_COLS = [
  { key: 'rfqId', label: 'RFQ ID' },
  { key: 'customerId', label: 'Customer' },
  { key: 'title', label: 'Title' },
  { key: 'status', label: 'Status' },
  { key: 'priority', label: 'Priority' },
  { key: 'lineCount', label: 'Lines' },
  { key: 'updatedAt', label: 'Age / Updated' },
];

export default function Inbox() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [total, setTotal] = useState(0);
  const toast = useToast();
  const nav = useNavigate();
  const debounceRef = useRef(null);

  // Read all filter/sort/page state from URL
  const statusFilter = searchParams.get('status') || '';
  const ownerFilter = searchParams.get('ownerId') || '';
  const customerFilter = searchParams.get('customerId') || '';
  const q = searchParams.get('q') || '';
  const sort = searchParams.get('sort') || 'updatedAt';
  const order = searchParams.get('order') || 'desc';
  const page = Number(searchParams.get('page') || '1');

  function setParam(key, value) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      // reset to page 1 on filter/sort change (not page itself)
      if (key !== 'page') next.delete('page');
      return next;
    });
  }

  function handleSortClick(col) {
    if (sort === col) {
      setParam('order', order === 'asc' ? 'desc' : 'asc');
    } else {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('sort', col);
        next.set('order', 'desc');
        next.delete('page');
        return next;
      });
    }
  }

  const reload = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (ownerFilter) params.ownerId = ownerFilter;
    if (customerFilter) params.customerId = customerFilter;
    if (q) params.q = q;
    if (sort) params.sort = sort;
    if (order) params.order = order;
    if (page > 1) params.page = page;

    const [rfqResp, custs] = await Promise.all([api.listRFQs(params), api.listCustomers()]);
    setItems(rfqResp.items);
    setTotal(rfqResp.total);
    setCustomers(custs.items);
    setLoading(false);
  }, [statusFilter, ownerFilter, customerFilter, q, sort, order, page]);

  useEffect(() => { reload(); }, [reload]);

  // Debounced search input — updates URL param after 200ms
  function handleSearchChange(e) {
    const val = e.target.value;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setParam('q', val);
    }, 200);
  }

  async function handleNew(body) {
    try {
      const rfq = await api.createRFQ(body);
      toast.push('RFQ created: ' + rfq.rfqId);
      setShowNew(false);
      nav(`/rfq/${rfq._id}/structure`);
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    }
  }

  const pageSize = 50;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function SortArrow({ col }) {
    if (sort !== col) return <span style={{ color: 'var(--ink-300)', marginLeft: 4 }}>↕</span>;
    return <span style={{ marginLeft: 4 }}>{order === 'asc' ? '↑' : '↓'}</span>;
  }

  return (
    <Shell
      title="RFQ Inbox"
      subtitle={loading ? 'Loading…' : `${total} RFQs`}
      actions={<button className="btn" onClick={() => setShowNew(true)}>+ New RFQ</button>}
    >
      {/* Status filter chips */}
      <div className="filter-bar">
        {['', 'New', 'Structured', 'SentToSuppliers', 'AwaitingQuotes', 'PartialQuotes', 'ReadyForComparison', 'DecisionMade', 'CustomerQuoteSent', 'Won', 'Lost'].map((s) => (
          <span key={s || 'all'} className={'chip ' + (statusFilter === s ? 'active' : 'muted')} onClick={() => setParam('status', s)}>
            {s || 'All'}
          </span>
        ))}
      </div>

      {/* Search box */}
      <div style={{ marginBottom: 12 }}>
        <input
          className="input"
          style={{ maxWidth: 320 }}
          placeholder="Search RFQ ID, title, customer…"
          defaultValue={q}
          onChange={handleSearchChange}
        />
      </div>

      {loading ? (
        <div className="empty">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty">
          <h3>No RFQs found</h3>
          <p>{q || statusFilter ? 'Try adjusting your filters.' : 'Create your first RFQ from an email, Excel, or by hand.'}</p>
          {!q && !statusFilter && <button className="btn" onClick={() => setShowNew(true)}>+ New RFQ</button>}
        </div>
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                {SORT_COLS.map(({ key, label }) => (
                  <th
                    key={key}
                    onClick={() => handleSortClick(key)}
                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                  >
                    {label}<SortArrow col={key} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const customer = customers.find((c) => String(c._id) === String(r.customerId));
                const ageMs = Date.now() - new Date(r.updatedAt).getTime();
                const ageH = ageMs / 3600000;
                const openStatuses = ['New', 'Structured', 'SentToSuppliers', 'AwaitingQuotes', 'PartialQuotes'];
                const isOpen = openStatuses.includes(r.status);
                const rowClass = isOpen && ageH > 96 ? 'row-aging-96' : isOpen && ageH > 48 ? 'row-aging-48' : '';
                return (
                  <tr key={r._id} className={rowClass}>
                    <td className="mono"><Link to={`/rfq/${r._id}`}>{r.rfqId}</Link></td>
                    <td>{customer ? customer.name : '—'}</td>
                    <td>{r.title}</td>
                    <td><span className={'chip ' + (STATUS_CHIP[r.status] || 'muted')}>{r.status}</span></td>
                    <td>{r.priority}</td>
                    <td>{r.lineCount}</td>
                    <td style={{ color: 'var(--ink-500)', fontSize: 12 }}>
                      {ageH > 96 && isOpen ? (
                        <span className="chip risk">⚠ {Math.round(ageH)}h</span>
                      ) : ageH > 48 && isOpen ? (
                        <span className="chip review">⚠ {Math.round(ageH)}h</span>
                      ) : (
                        new Date(r.updatedAt).toLocaleString()
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, fontSize: 13 }}>
              <button
                className="btn btn-secondary"
                disabled={page <= 1}
                onClick={() => setParam('page', String(page - 1))}
              >
                ← Prev
              </button>
              <span style={{ color: 'var(--ink-500)' }}>
                Page {page} of {totalPages} ({total} total)
              </span>
              <button
                className="btn btn-secondary"
                disabled={page >= totalPages}
                onClick={() => setParam('page', String(page + 1))}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {showNew && <NewRFQModal onClose={() => setShowNew(false)} customers={customers} onCreate={handleNew} />}
    </Shell>
  );
}

function NewRFQModal({ onClose, customers, onCreate }) {
  const [form, setForm] = useState({
    customerId: customers[0]?._id || '',
    title: '',
    source: 'Manual',
    priority: 'Normal',
    currencyPref: 'USD',
    quoteMode: 'DirectEXW',
    notes: '',
    originalEmailBody: '',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10, 10, 30, 0.5)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="card" style={{ width: 520, background: '#fff' }}>
        <h2 className="card-title">New RFQ</h2>
        <label className="field">
          <span className="label">Customer</span>
          <select className="select" value={form.customerId} onChange={(e) => set('customerId', e.target.value)}>
            {customers.map((c) => (<option key={c._id} value={c._id}>{c.name}</option>))}
          </select>
        </label>
        <label className="field">
          <span className="label">Title</span>
          <input className="input" value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Bearings package Q2" />
        </label>
        <div className="row">
          <label className="field">
            <span className="label">Priority</span>
            <select className="select" value={form.priority} onChange={(e) => set('priority', e.target.value)}>
              {['Low', 'Normal', 'High', 'Critical'].map((p) => <option key={p}>{p}</option>)}
            </select>
          </label>
          <label className="field">
            <span className="label">Currency</span>
            <select className="select" value={form.currencyPref} onChange={(e) => set('currencyPref', e.target.value)}>
              {['USD', 'EUR', 'GBP', 'INR', 'AED', 'SGD'].map((p) => <option key={p}>{p}</option>)}
            </select>
          </label>
          <label className="field">
            <span className="label">Quote mode</span>
            <select className="select" value={form.quoteMode} onChange={(e) => set('quoteMode', e.target.value)}>
              <option value="DirectEXW">Direct / EXW</option>
              <option value="CNERZY_Margin">CNERZY Margin</option>
            </select>
          </label>
        </div>
        <label className="field">
          <span className="label">Original email body (optional)</span>
          <textarea className="input" rows={5} value={form.originalEmailBody} onChange={(e) => set('originalEmailBody', e.target.value)} />
        </label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn" onClick={() => onCreate(form)} disabled={!form.customerId || !form.title}>Create</button>
        </div>
      </div>
    </div>
  );
}
